import { useCallback } from "react";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import type { NoticeConversation, NoticeFilterId } from "./notice-conversations";
import type { MentorWorkspaceId } from "./workspace";

export type NoticeRowSection = "flat" | "grouped";

export function noticeBadgeViewProps(
  conversation: NoticeConversation,
  listPosition: number,
): Record<string, unknown> | null {
  if (conversation.badge === null) return null;
  return {
    conversation_id: conversation.id,
    badge_kind: conversation.badge.kind,
    list_position: listPosition,
  };
}

export function noticeRowOpenProps(
  conversation: NoticeConversation,
  listPosition: number,
  section: NoticeRowSection,
): Record<string, unknown> {
  return {
    conversation_id: conversation.id,
    badge_kind: conversation.badge?.kind ?? null,
    list_position: listPosition,
    section,
  };
}

export function useNoticeAnalytics() {
  const { track } = useTrackEvent();

  const trackWorkspaceSwitch = useCallback(
    (from: MentorWorkspaceId, to: MentorWorkspaceId) => {
      if (from === to) return;
      track(PostHogEvents.NOTICE_WORKSPACE_SWITCH, {
        from_workspace: from,
        to_workspace: to,
      });
    },
    [track],
  );

  const trackFilterApply = useCallback(
    (filter: NoticeFilterId, count: number) =>
      track(PostHogEvents.NOTICE_FILTER_APPLY, { filter, count }),
    [track],
  );

  const trackSearchOpen = useCallback(
    (workspace: MentorWorkspaceId) =>
      track(PostHogEvents.NOTICE_SEARCH_OPEN, { workspace }),
    [track],
  );

  // Raw search text is a teen/channel name (PII), so only the length is tracked.
  const trackSearchUse = useCallback(
    (query: string) =>
      track(PostHogEvents.NOTICE_SEARCH_USE, { query_chars: query.trim().length }),
    [track],
  );

  const trackBadgeView = useCallback(
    (conversation: NoticeConversation, listPosition: number) => {
      const props = noticeBadgeViewProps(conversation, listPosition);
      if (props !== null) track(PostHogEvents.NOTICE_BADGE_VIEW, props);
    },
    [track],
  );

  const trackRowOpen = useCallback(
    (
      conversation: NoticeConversation,
      listPosition: number,
      section: NoticeRowSection,
    ) =>
      track(
        PostHogEvents.NOTICE_ROW_OPEN,
        noticeRowOpenProps(conversation, listPosition, section),
      ),
    [track],
  );

  return {
    trackWorkspaceSwitch,
    trackFilterApply,
    trackSearchOpen,
    trackSearchUse,
    trackBadgeView,
    trackRowOpen,
  };
}
